/**
 * StepDashboard.tsx — Step 3: Campaign management dashboard
 *
 * Shows per-post rows with status badges, scheduled time, and actions.
 * Polls GET /api/campaigns/:id every 30s to pick up cron-published posts.
 *
 * Actions:
 *  [Preview]    → opens PostPreviewDrawer
 *  [✏ pencil]   → inline datetime-local editor (pending/paused only)
 *  [Skip]       → POST /api/campaigns/:id/posts/:pid/skip (optimistic UI)
 *  [Pause]      → POST /api/campaigns/:id/pause
 *  [Resume]     → POST /api/campaigns/:id/resume
 *  [Delete]     → confirm modal → DELETE /api/campaigns/:id → reset to Step 0
 *
 * RI-011: LinkedIn share URL handler — clipboard (no-await) → window.open,
 *         no 'noopener' in third arg.
 *
 * Order warning: if post N is scheduled at or after post N+1, PostRow shows
 *   "⚠ after Day N+1" next to the scheduled time.
 */

import React, { useState, useEffect, useRef } from "react";
import PostPreviewDrawer from "./PostPreviewDrawer";
import {
  card, primaryBtn, secondaryBtn, ghostBtn, stepHeading, stepSub,
  ACCENT, ACCENT_08, spinnerStyle,
} from "../DistillPageV2/sharedStyles";
import type { Campaign, CampaignPost, PostStatus } from "./types";
import { ROLE_LABELS, ROLE_COLORS } from "./types";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  campaign:          Campaign;
  onPollTick:        () => Promise<Campaign>;
  onPause:           () => Promise<void>;
  onResume:          () => Promise<void>;
  onSkipPost:        (pid: number) => Promise<void>;
  onReschedulePost:  (pid: number, scheduledAt: string) => Promise<void>;
  onDelete:          () => Promise<void>;
  onReset:           () => void;   // resets page to Step 0 after delete
  authHeaders:       Record<string, string>;
  apiUrl:            string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const POLL_MS = 30_000;

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

/** Convert UTC ISO → YYYY-MM-DDTHH:MM suitable for datetime-local input (browser local TZ). */
function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert datetime-local string (local TZ) → UTC ISO string. */
function datetimeLocalToIso(local: string): string {
  return new Date(local).toISOString();
}

const STATUS_BADGE: Record<PostStatus, { label: string; bg: string; color: string }> = {
  pending:    { label: 'Scheduled',   bg: '#F0F0EE', color: '#666'    },
  publishing: { label: 'Publishing…', bg: '#FFF3CD', color: '#856404' },
  published:  { label: 'Published',   bg: '#D1F5E4', color: '#166534' },
  failed:     { label: 'Failed',      bg: '#FFE4E4', color: '#C0392B' },
  skipped:    { label: 'Skipped',     bg: '#F0F0EE', color: '#aaa'    },
  paused:     { label: 'Paused',      bg: '#FFF3CD', color: '#856404' },
};

const CAMPAIGN_STATUS_BADGE: Record<string, { label: string; color: string }> = {
  active:    { label: 'Active',    color: '#166534' },
  paused:    { label: 'Paused',    color: '#856404' },
  completed: { label: 'Completed', color: '#1d4ed8' },
  cancelled: { label: 'Cancelled', color: '#aaa'    },
};

// ── PostRow ───────────────────────────────────────────────────────────────────

interface PostRowProps {
  post:            CampaignPost;
  slot?:           { role: string };
  hasOrderWarning: boolean;    // true if this post is scheduled at/after the next
  nextDayNumber:   number | null;
  onPreview:       () => void;
  onSkip:          () => void;
  onReschedule:    (pid: number, scheduledAt: string) => Promise<void>;
  isSkipping:      boolean;
}

const PostRow: React.FC<PostRowProps> = ({
  post, slot, hasOrderWarning, nextDayNumber,
  onPreview, onSkip, onReschedule, isSkipping,
}) => {
  const [isEditing,     setIsEditing]     = useState(false);
  const [draftSchedule, setDraftSchedule] = useState('');
  const [isSaving,      setIsSaving]      = useState(false);
  const [saveError,     setSaveError]     = useState<string | null>(null);

  const badge     = STATUS_BADGE[post.status] ?? STATUS_BADGE.pending;
  const roleColor = ROLE_COLORS[slot?.role ?? ''] ?? ACCENT;
  const canEdit   = post.status === 'pending' || post.status === 'paused';

  const handleShareClick = () => {
    if (!post.linkedinShareUrl) return;
    // RI-011: clipboard first (no await), then window.open without noopener
    navigator.clipboard.writeText(post.linkedinShareUrl).catch(() => {});
    window.open(post.linkedinShareUrl, '_blank');
  };

  const startEdit = () => {
    setDraftSchedule(isoToDatetimeLocal(post.scheduledAt));
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const saveSchedule = async () => {
    if (!draftSchedule) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onReschedule(post.id, datetimeLocalToIso(draftSchedule));
      setIsEditing(false);
    } catch {
      setSaveError('Failed to update — please try again');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{
      padding:      "12px 0",
      borderBottom: "1px solid #F0F0EE",
    }}>
      {/* ── Top row: day badge + title + status + actions ── */}
      <div style={{
        display:    "flex",
        alignItems: "center",
        gap:        "10px",
        flexWrap:   "wrap",
      }}>
        {/* Day badge */}
        <div style={{
          background:   `${roleColor}14`,
          color:        roleColor,
          borderRadius: "6px",
          padding:      "2px 8px",
          fontSize:     "11px",
          fontWeight:   700,
          flexShrink:   0,
        }}>
          Day {post.dayNumber}
        </div>

        {/* Title */}
        <div style={{ flex: 1, minWidth: 0, fontSize: "13px", fontWeight: 600, color: "#333" }}>
          {ROLE_LABELS[slot?.role ?? ''] ?? `Day ${post.dayNumber}`}
        </div>

        {/* Status badge */}
        <div style={{
          background:   badge.bg,
          color:        badge.color,
          borderRadius: "20px",
          padding:      "3px 10px",
          fontSize:     "11px",
          fontWeight:   700,
          flexShrink:   0,
        }}>
          {badge.label}
        </div>

        {/* View on LinkedIn — published posts only */}
        {post.status === 'published' && post.linkedinShareUrl && (
          <button
            data-testid={`cp-share-btn-${post.id}`}
            onClick={handleShareClick}
            title="Copy URL and open on LinkedIn"
            style={{
              display:      "inline-flex",
              alignItems:   "center",
              gap:          "4px",
              background:   "#E8F0FE",
              color:        "#1a73e8",
              border:       "none",
              borderRadius: "8px",
              padding:      "5px 10px",
              fontSize:     "12px",
              fontWeight:   700,
              cursor:       "pointer",
              fontFamily:   "inherit",
              flexShrink:   0,
              whiteSpace:   "nowrap",
            }}
          >
            View post ↗
          </button>
        )}

        {/* Preview */}
        <button
          data-testid={`cp-preview-btn-${post.id}`}
          onClick={onPreview}
          style={{
            background:   ACCENT_08,
            color:        ACCENT,
            border:       "none",
            borderRadius: "8px",
            padding:      "5px 10px",
            fontSize:     "12px",
            fontWeight:   700,
            cursor:       "pointer",
            fontFamily:   "inherit",
            flexShrink:   0,
          }}
        >
          Preview
        </button>

        {/* Skip */}
        {(post.status === 'pending' || post.status === 'paused') && (
          <button
            data-testid={`cp-skip-btn-${post.id}`}
            onClick={onSkip}
            disabled={isSkipping}
            style={{
              background:   "none",
              color:        "#aaa",
              border:       "1.5px solid #E4E4E0",
              borderRadius: "8px",
              padding:      "4px 10px",
              fontSize:     "12px",
              fontWeight:   600,
              cursor:       isSkipping ? "not-allowed" : "pointer",
              fontFamily:   "inherit",
              flexShrink:   0,
            }}
          >
            {isSkipping ? "…" : "Skip"}
          </button>
        )}
      </div>

      {/* ── Scheduled time row ── */}
      <div style={{ marginTop: "6px", paddingLeft: "2px" }}>
        {isEditing ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <input
              data-testid={`cp-schedule-input-${post.id}`}
              type="datetime-local"
              value={draftSchedule}
              onChange={e => setDraftSchedule(e.target.value)}
              style={{
                fontSize:   "12px",
                padding:    "4px 8px",
                border:     "1.5px solid #5B4FE8",
                borderRadius: "6px",
                fontFamily: "inherit",
                color:      "#333",
                outline:    "none",
              }}
            />
            <button
              data-testid={`cp-schedule-save-${post.id}`}
              onClick={saveSchedule}
              disabled={isSaving || !draftSchedule}
              style={{
                background:   ACCENT,
                color:        "#fff",
                border:       "none",
                borderRadius: "6px",
                padding:      "4px 12px",
                fontSize:     "12px",
                fontWeight:   700,
                cursor:       isSaving ? "not-allowed" : "pointer",
                fontFamily:   "inherit",
                opacity:      isSaving ? 0.7 : 1,
              }}
            >
              {isSaving ? "…" : "Save"}
            </button>
            <button
              data-testid={`cp-schedule-cancel-${post.id}`}
              onClick={cancelEdit}
              style={{
                background:   "none",
                color:        "#888",
                border:       "1.5px solid #E4E4E0",
                borderRadius: "6px",
                padding:      "4px 10px",
                fontSize:     "12px",
                fontWeight:   600,
                cursor:       "pointer",
                fontFamily:   "inherit",
              }}
            >
              Cancel
            </button>
            {saveError && (
              <span style={{ fontSize: "11px", color: "#C0392B" }}>{saveError}</span>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: "#555", fontWeight: 500 }}>
              🗓 {formatDate(post.scheduledAt)}
            </span>
            {canEdit && (
              <button
                data-testid={`cp-reschedule-btn-${post.id}`}
                onClick={startEdit}
                title="Change schedule"
                style={{
                  background: "none",
                  border:     "none",
                  cursor:     "pointer",
                  padding:    "1px 4px",
                  color:      "#bbb",
                  fontSize:   "12px",
                  lineHeight: 1,
                  borderRadius: "4px",
                }}
              >
                ✏
              </button>
            )}
            {hasOrderWarning && nextDayNumber !== null && (
              <span
                data-testid={`cp-order-warning-${post.id}`}
                style={{ fontSize: "11px", color: "#D97706", fontWeight: 600 }}
              >
                ⚠ after Day {nextDayNumber}
              </span>
            )}
            {post.lastError && (
              <span style={{ fontSize: "11px", color: "#C0392B" }}>
                Error: {post.lastError}
                {post.retryCount > 0 && ` (attempt ${post.retryCount})`}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── DeleteModal ───────────────────────────────────────────────────────────────

interface DeleteModalProps {
  onConfirm:  () => void;
  onCancel:   () => void;
  isDeleting: boolean;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ onConfirm, onCancel, isDeleting }) => (
  <>
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 900,
      }}
    />
    <div style={{
      position:     "fixed",
      top:          "50%",
      left:         "50%",
      transform:    "translate(-50%, -50%)",
      background:   "#fff",
      borderRadius: "16px",
      padding:      "28px 24px",
      zIndex:       901,
      width:        "min(400px, 90vw)",
      boxShadow:    "0 8px 40px rgba(0,0,0,0.18)",
      textAlign:    "center",
    }}>
      <div style={{ fontSize: "20px", marginBottom: "8px" }}>🗑</div>
      <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "8px" }}>
        Delete campaign?
      </div>
      <div style={{ fontSize: "14px", color: "#666", marginBottom: "24px", lineHeight: "1.5" }}>
        This will delete the campaign and all {7} posts permanently.
        Scheduled posts will not be published.
      </div>
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          data-testid="cp-delete-cancel"
          onClick={onCancel}
          style={{ ...secondaryBtn, flex: 1, textAlign: "center" }}
        >
          Cancel
        </button>
        <button
          data-testid="cp-delete-confirm"
          onClick={onConfirm}
          disabled={isDeleting}
          style={{
            ...primaryBtn,
            flex:           1,
            background:     "#E84F4F",
            justifyContent: "center",
            opacity:        isDeleting ? 0.6 : 1,
          }}
        >
          {isDeleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </>
);

// ── Main component ────────────────────────────────────────────────────────────

const StepDashboard: React.FC<Props> = ({
  campaign: initialCampaign,
  onPollTick, onPause, onResume, onSkipPost, onReschedulePost, onDelete, onReset,
  authHeaders, apiUrl,
}) => {
  const [campaign,    setCampaign]    = useState<Campaign>(initialCampaign);
  const [previewPost, setPreviewPost] = useState<CampaignPost | null>(null);
  const [showDelete,  setShowDelete]  = useState(false);
  const [isDeleting,  setIsDeleting]  = useState(false);
  const [isPausing,   setIsPausing]   = useState(false);
  const [isResuming,  setIsResuming]  = useState(false);
  const [skippingIds, setSkippingIds] = useState<Set<number>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync if parent pushes fresh campaign (e.g. after lock)
  useEffect(() => { setCampaign(initialCampaign); }, [initialCampaign]);

  // ── Poll every 30s — stop when completed or cancelled ────────────────────
  useEffect(() => {
    if (campaign.status === 'completed' || campaign.status === 'cancelled') return;

    intervalRef.current = setInterval(async () => {
      try {
        const updated = await onPollTick();
        setCampaign(updated);
        if (updated.status === 'completed' || updated.status === 'cancelled') {
          if (intervalRef.current !== null) clearInterval(intervalRef.current);
        }
      } catch { /* transient errors — keep polling */ }
    }, POLL_MS);

    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [campaign.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handlePause = async () => {
    setIsPausing(true);
    try {
      await onPause();
      const updated = await onPollTick();
      setCampaign(updated);
    } finally { setIsPausing(false); }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      await onResume();
      const updated = await onPollTick();
      setCampaign(updated);
    } finally { setIsResuming(false); }
  };

  const handleSkip = async (post: CampaignPost) => {
    setSkippingIds(s => new Set(s).add(post.id));
    try {
      await onSkipPost(post.id);
      // Optimistic update
      setCampaign(prev => ({
        ...prev,
        posts: prev.posts.map(p => p.id === post.id ? { ...p, status: 'skipped' } : p),
      }));
    } finally {
      setSkippingIds(s => { const n = new Set(s); n.delete(post.id); return n; });
    }
  };

  const handleReschedule = async (pid: number, scheduledAt: string) => {
    await onReschedulePost(pid, scheduledAt);
    // Optimistic update — reflect new time immediately
    setCampaign(prev => ({
      ...prev,
      posts: prev.posts.map(p => p.id === pid ? { ...p, scheduledAt } : p),
    }));
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      onReset();
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const campaignBadge = CAMPAIGN_STATUS_BADGE[campaign.status] ?? { label: campaign.status, color: "#aaa" };
  const isActive      = campaign.status === 'active';
  const isPaused      = campaign.status === 'paused';
  const isCompleted   = campaign.status === 'completed';

  // Order violations: post[i] is a violation if it's scheduled >= post[i+1]
  const sortedPosts = [...campaign.posts].sort((a, b) => a.dayNumber - b.dayNumber);
  const orderViolations = new Set<number>();
  for (let i = 0; i < sortedPosts.length - 1; i++) {
    if (new Date(sortedPosts[i].scheduledAt) >= new Date(sortedPosts[i + 1].scheduledAt)) {
      orderViolations.add(sortedPosts[i].id);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
          <div style={stepHeading}>{campaign.name}</div>
          <div style={{
            background:   `${campaignBadge.color}18`,
            color:        campaignBadge.color,
            borderRadius: "20px",
            padding:      "3px 10px",
            fontSize:     "12px",
            fontWeight:   700,
          }}>
            {campaignBadge.label}
          </div>
        </div>
        <div style={stepSub}>{campaign.topic}</div>
      </div>

      {/* ── Campaign complete banner ───────────────────────────────────────── */}
      {isCompleted && (
        <div style={{
          background:   "#D1F5E4",
          border:       "1.5px solid #2DB88F",
          borderRadius: "12px",
          padding:      "14px 16px",
          fontSize:     "14px",
          color:        "#166534",
          fontWeight:   600,
        }}>
          🎉 Campaign complete — all {campaign.totalDays} posts published.
        </div>
      )}

      {/* ── Controls ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {isActive && (
          <button
            data-testid="cp-pause-btn"
            onClick={handlePause}
            disabled={isPausing}
            style={{
              ...secondaryBtn,
              opacity: isPausing ? 0.6 : 1,
              display: "inline-flex", alignItems: "center", gap: "6px",
            }}
          >
            {isPausing ? <><span style={spinnerStyle} />Pausing…</> : "⏸ Pause"}
          </button>
        )}
        {isPaused && (
          <button
            data-testid="cp-resume-btn"
            onClick={handleResume}
            disabled={isResuming}
            style={{
              ...primaryBtn,
              opacity: isResuming ? 0.6 : 1,
              display: "inline-flex", alignItems: "center", gap: "6px",
            }}
          >
            {isResuming ? <><span style={spinnerStyle} />Resuming…</> : "▶ Resume"}
          </button>
        )}
        <button
          data-testid="cp-delete-btn"
          onClick={() => setShowDelete(true)}
          style={{
            ...ghostBtn,
            color:        "#E84F4F",
            border:       "1.5px solid #E84F4F",
            borderRadius: "12px",
            padding:      "8px 16px",
          }}
        >
          🗑 Delete
        </button>
      </div>

      {/* ── Post rows ─────────────────────────────────────────────────────── */}
      <div style={card}>
        {campaign.posts.length === 0 && (
          <div style={{ color: "#aaa", fontSize: "14px", textAlign: "center", padding: "20px 0" }}>
            No posts yet.
          </div>
        )}
        {sortedPosts.map((post, i) => (
          <PostRow
            key={post.id}
            post={post}
            slot={campaign.storyArc?.[i]}
            hasOrderWarning={orderViolations.has(post.id)}
            nextDayNumber={sortedPosts[i + 1]?.dayNumber ?? null}
            onPreview={() => setPreviewPost(post)}
            onSkip={() => handleSkip(post)}
            onReschedule={handleReschedule}
            isSkipping={skippingIds.has(post.id)}
          />
        ))}
      </div>

      {/* ── Schedule info ─────────────────────────────────────────────────── */}
      <div style={{ fontSize: "12px", color: "#aaa", textAlign: "center" }}>
        Posts publish daily at {campaign.dailyTime} {campaign.timezone}
      </div>

      {/* ── Delete modal ──────────────────────────────────────────────────── */}
      {showDelete && (
        <DeleteModal
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
          isDeleting={isDeleting}
        />
      )}

      {/* ── Preview drawer ────────────────────────────────────────────────── */}
      <PostPreviewDrawer
        campaignId={campaign.id}
        post={previewPost}
        onClose={() => setPreviewPost(null)}
        authHeaders={authHeaders}
        apiUrl={apiUrl}
      />
    </div>
  );
};

export default StepDashboard;
